import { useState } from "react";
import {
  CheckCircle2,
  Phone,
  Mail,
  MessageSquare,
  ArrowRight,
  Loader2,
  X,
  Sparkles,
  Rocket,
  Gift,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function EndDemoSummary({ summary, sessionInfo, onClose, loading, onTryChat }) {
  // step: "summary" → schermata riassunto | "convince" → schermata persuasiva pre-chiusura
  const [step, setStep] = useState("summary");

  if (loading) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="end-demo-loading"
      >
        <div className="bg-white rounded-2xl p-10 flex flex-col items-center gap-4 max-w-sm">
          <Loader2 className="w-12 h-12 animate-spin text-brand-olive" />
          <p className="font-heading text-xl text-brand-ink text-center">
            Lara sta preparando il rapporto e te lo sta inviando…
          </p>
          <p className="text-sm text-brand-inkSoft text-center">
            Riceverai il riassunto della chiamata via email tra pochi secondi.
          </p>
        </div>
      </div>
    );
  }

  if (!summary) return null;

  // ====== SCHERMATA "TI HA CONVINTO?" (dopo che l'utente clicca "Chiudi") ======
  if (step === "convince") {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        data-testid="convince-modal"
      >
        <div className="bg-white rounded-2xl max-w-lg w-full my-8 shadow-2xl overflow-hidden">
          {/* Header con sfondo caldo */}
          <div className="bg-gradient-to-br from-brand-terra to-brand-olive p-8 text-white text-center relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition"
              data-testid="convince-close-x"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
            <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-90" strokeWidth={1.5} />
            <h2 className="font-heading text-3xl leading-tight">Ti ha convinto?</h2>
            <p className="mt-3 text-white/90 text-sm leading-relaxed max-w-sm mx-auto">
              Questa stessa voce, sul tuo numero, può rispondere ai tuoi clienti{" "}
              <strong>24 ore su 24</strong>. Niente chiamate perse, niente clienti frustrati.
            </p>
          </div>

          {/* Body */}
          <div className="p-6 md:p-8 space-y-5">
            <div className="grid grid-cols-1 gap-3">
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="w-5 h-5 text-brand-olive shrink-0 mt-0.5" />
                <p className="text-sm text-brand-ink leading-relaxed">
                  <strong>Pronta da oggi stesso</strong> sul tuo numero, senza cambiare nulla.
                </p>
              </div>
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="w-5 h-5 text-brand-olive shrink-0 mt-0.5" />
                <p className="text-sm text-brand-ink leading-relaxed">
                  Riassunto di ogni chiamata su <strong>email</strong> in pochi secondi.
                </p>
              </div>
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="w-5 h-5 text-brand-olive shrink-0 mt-0.5" />
                <p className="text-sm text-brand-ink leading-relaxed">
                  Voce <strong>premium reale</strong>, addestrata sulla tua azienda.
                </p>
              </div>
            </div>

            <div className="bg-brand-bg2 border border-brand-line rounded-xl p-5 text-center">
              <p className="text-xs font-medium tracking-[0.2em] text-brand-terra uppercase mb-1">
                Offerta lancio
              </p>
              <p className="font-heading text-3xl text-brand-ink">588€ / anno</p>
              <p className="text-xs text-brand-inkSoft mt-1">piattaforma · chiamate a consumo, al costo</p>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="p-6 md:p-8 border-t border-brand-line flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="px-5 py-3 rounded-lg border border-brand-line text-brand-inkSoft hover:bg-brand-bg2 transition flex-1 text-sm"
              data-testid="convince-decline"
            >
              No grazie, chiudi
            </button>
            <Link
              to="/attivazione"
              className="btn-olive px-5 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1"
              data-testid="convince-cta"
            >
              <Rocket className="w-4 h-4" />
              Voglio 24Vox subito
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ====== SCHERMATA RIASSUNTO (default) ======
  const fields = [
    { label: "Cliente", value: summary.nome_cliente, icon: CheckCircle2 },
    { label: "Motivo", value: summary.motivo_chiamata, icon: MessageSquare },
    { label: "Contatti", value: summary.contatti_lasciati, icon: Phone },
    { label: "Info chiave", value: summary.info_chiave, icon: CheckCircle2 },
    { label: "Cosa fare", value: summary.azione_richiesta, icon: ArrowRight },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="end-demo-summary"
    >
      <div className="bg-white rounded-2xl max-w-lg w-full my-8 shadow-2xl">
        <div className="p-6 md:p-8 border-b border-brand-line">
          <p className="text-xs font-medium tracking-[0.2em] text-brand-terra uppercase mb-2">
            Demo terminata
          </p>
          <h2 className="font-heading text-2xl md:text-3xl text-brand-ink leading-tight">
            Ecco cosa avresti ricevuto via email.
          </h2>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          {summary.riassunto_breve && (
            <div
              className="bg-brand-bg2 rounded-xl p-5 border border-brand-line"
              data-testid="summary-short"
            >
              <p className="text-sm text-brand-inkSoft mb-1 italic">Messaggio per il titolare</p>
              <p className="text-brand-ink leading-relaxed">{summary.riassunto_breve}</p>
            </div>
          )}

          <div className="space-y-3">
            {fields.map((f, i) => (
              <div
                key={i}
                className="flex gap-3 items-start"
                data-testid={`summary-field-${f.label.toLowerCase().replace(" ", "-")}`}
              >
                <f.icon className="w-4 h-4 text-brand-olive shrink-0 mt-1" strokeWidth={2} />
                <div className="flex-1">
                  <p className="text-xs font-medium text-brand-inkSoft uppercase tracking-wider">
                    {f.label}
                  </p>
                  <p className="text-sm text-brand-ink mt-0.5">{f.value || "—"}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-brand-olive/5 border border-brand-olive/20 rounded-xl p-5">
            <p className="text-sm text-brand-ink leading-relaxed">
              <strong>Quando la 24Vox è attiva sul tuo numero</strong>, questo riassunto ti arriva su
              email pochi secondi dopo che il cliente riaggancia.
            </p>
          </div>

          {onTryChat && (
            <button
              onClick={onTryChat}
              className="w-full text-left bg-gradient-to-br from-brand-terra/10 to-brand-olive/10 border border-brand-terra/30 rounded-xl p-5 hover:from-brand-terra/15 hover:to-brand-olive/15 transition group"
              data-testid="end-demo-try-chat"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-terra flex items-center justify-center shrink-0">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-semibold tracking-[0.18em] text-brand-terra uppercase">
                    In omaggio sul tuo sito
                  </p>
                  <p className="font-heading text-lg text-brand-ink leading-tight mt-1">
                    Vuoi provare la chat con il tuo nuovo assistente?
                  </p>
                  <p className="text-sm text-brand-inkSoft mt-1 leading-snug">
                    Stessa AI della telefonata, ma in chat. È la bolla che metteremo sul tuo sito,
                    <strong> inclusa nel piano</strong>.
                  </p>
                  <p className="mt-2 text-sm font-semibold text-brand-terra flex items-center gap-1 group-hover:gap-2 transition-all">
                    Prova la chat ora <ArrowRight className="w-4 h-4" />
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Footer: 3 pulsanti */}
        <div className="p-6 md:p-8 border-t border-brand-line grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={onClose}
            className="px-4 py-3 rounded-lg border border-brand-line text-brand-inkSoft hover:bg-brand-bg2 transition text-sm font-medium flex items-center justify-center gap-2"
            data-testid="end-demo-retry"
          >
            <Phone className="w-4 h-4" />
            Riprova la telefonata
          </button>

          <Link
            to="/attivazione"
            className="btn-olive px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
            data-testid="end-demo-cta"
          >
            <Mail className="w-4 h-4" />
            Attiva la tua 24Vox
          </Link>

          <button
            onClick={() => setStep("convince")}
            className="px-4 py-3 rounded-lg border border-brand-line text-brand-inkSoft hover:bg-brand-bg2 transition text-sm font-medium flex items-center justify-center gap-2"
            data-testid="end-demo-close"
          >
            <X className="w-4 h-4" />
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
